import { ComponentProps, forwardRef } from 'react';
import { withStyles } from '../../../utils';
import styles from './NoFillButton.module.scss';
import MaterialSymbol from '../../DataDisplay/MaterialSymbol/MaterialSymbol';
import { useDatadogLog } from '../logging';

const cn = withStyles(styles);

type ColorScheme = 'neutral' | 'destructive';
type Size = 'large' | 'standard' | 'small' | 'smallest';
type IconProps = ComponentProps<typeof MaterialSymbol>;

type NoFillButtonProps = React.ComponentProps<'button'> & {
  colorScheme?: ColorScheme;
  size?: Size;
  iconLeft?: IconProps['icon'];
  iconLeftProps?: IconProps;
  iconRight?: IconProps['icon'];
  iconRightProps?: IconProps;
};

const NoFillButton = forwardRef<HTMLButtonElement, NoFillButtonProps>(
  (
    {
      children,
      colorScheme = 'neutral',
      size = 'standard',
      className,
      style,
      iconLeft,
      iconLeftProps,
      iconRight,
      iconRightProps,
      ...props
    },
    forwardedRef,
  ) => {
    const log = useDatadogLog();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      const label =
        typeof children === 'string' ? children : (props['aria-label'] ?? e.currentTarget.textContent ?? 'Button');

      log({
        message: `${label} clicked`,
        payload: { name: label },
        actionType: 'Button',
      });

      if (props.onClick) {
        props.onClick(e);
      }
    };

    return (
      <button
        ref={forwardedRef}
        type="button"
        {...props}
        className={cn(
          'noFillButton',
          `color-${colorScheme}`,
          `size-${size}`,
          (iconLeftProps || iconLeft) && 'iconLeft',
          (iconRightProps || iconRight) && 'iconRight',
          className,
        )}
        style={{
          ...style,
        }}
        onClick={handleClick}
        data-custom="true"
      >
        {iconLeftProps && <MaterialSymbol {...iconLeftProps} className={cn('icon', iconLeftProps.className)} />}
        {iconLeft && <MaterialSymbol className={cn('icon')} icon={iconLeft} />}
        {children}
        {iconRight && <MaterialSymbol className={cn('icon')} icon={iconRight} />}
        {iconRightProps && <MaterialSymbol {...iconRightProps} className={cn('icon', iconRightProps.className)} />}
      </button>
    );
  },
);

NoFillButton.displayName = 'NoFillButton';

export default NoFillButton;
